/**
 * rosterUpdate.js — Sleeper current roster fetcher
 *
 * Fetches live NFL player/team data from Sleeper and maps it onto
 * UNIQUE_PLAYERS. Normalizes Sleeper's team abbreviations to match
 * Mock1's 3-letter format.
 *
 * Cache: localStorage 'mock1_roster_cache' with 24-hour TTL.
 */

const SLEEPER_PLAYERS_URL = "https://api.sleeper.app/v1/players/nfl";
const CACHE_KEY = "mock1_roster_cache";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Sleeper abbrev → Mock1 abbrev for known mismatches
const TEAM_NORM = {
  JAX: "JAC",   // Jacksonville
  SFO: "SF",    // San Francisco
  GBP: "GB",    // Green Bay (rare alternate)
  NOS: "NO",    // New Orleans (rare alternate)
  NEP: "NE",    // New England (rare alternate)
  KCC: "KC",    // Kansas City (rare alternate)
  LVR: "LV",    // Las Vegas (older code)
  ARZ: "ARI",   // Arizona (rare alternate)
  HST: "HOU",   // Houston (rare alternate)
  BLT: "BAL",   // Baltimore (rare alternate)
  CLV: "CLE",   // Cleveland (rare alternate)
  PHL: "PHI",   // Philadelphia (rare alternate)
};

function normalizeTeam(team) {
  if (!team) return null;
  const upper = team.toUpperCase();
  return TEAM_NORM[upper] ?? upper;
}

// Sleeper position strings → Mock1 positions (keep only fantasy-relevant skill positions)
const POS_NORM = {
  QB: "QB", RB: "RB", WR: "WR", TE: "TE",
  K: "K", DEF: "DEF",
};

function normalizePos(pos) {
  if (!pos) return null;
  return POS_NORM[pos.toUpperCase()] ?? null;
}

/**
 * Normalize a player name for fuzzy matching:
 * - Collapse whitespace
 * - Strip trailing periods from suffixes (Jr. → Jr, Sr. → Sr)
 * - Lowercase for comparison
 *
 * e.g. "Travis Etienne Jr." → "travis etienne jr"
 *      "Kenneth Walker III" → "kenneth walker iii"
 *      "Amon-Ra St. Brown"  → "amon-ra st. brown"  (mid-name periods kept)
 */
function normalizeName(name) {
  return name
    .trim()
    .replace(/\s+/g, " ")
    // Strip trailing period only from known suffix tokens at end of string
    .replace(/\b(Jr|Sr|II|III|IV|V)\.$/, "$1")
    .toLowerCase();
}

/**
 * Strip generational suffix (Jr, Sr, II, III, IV, V) from a normalized name.
 * Used as a fallback match key.
 */
function stripSuffix(normalized) {
  return normalized.replace(/\s+(jr|sr|ii|iii|iv|v)$/, "").trim();
}

/**
 * Returns cached roster data if present and less than 24 hours old.
 * Returns null if cache is missing or stale.
 *
 * @returns {{ data: Map<string, {team: string, pos: string}>, fetchedAt: number } | null}
 */
export function getCachedRosters() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || !parsed?.entries) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return {
      data: new Map(parsed.entries),
      fetchedAt: parsed.fetchedAt,
    };
  } catch {
    return null;
  }
}

/**
 * Fetches current NFL player/team data from Sleeper API.
 * Returns a Map of playerName → { team, pos }.
 *
 * Builds two lookup indices internally:
 *   - exact normalized name  (primary)
 *   - suffix-stripped name   (fallback for Jr/Sr/III mismatches)
 *
 * Result is cached in localStorage for 24 hours.
 *
 * @returns {Promise<Map<string, {team: string, pos: string}>>}
 */
export async function fetchCurrentRosters() {
  const res = await fetch(SLEEPER_PLAYERS_URL);
  if (!res.ok) throw new Error(`Sleeper players fetch failed: ${res.status}`);
  const raw = await res.json();

  const entries = []; // [name, {team, pos}] pairs for Map constructor + cache serialization

  for (const player of Object.values(raw)) {
    const pos = normalizePos(player.fantasy_positions?.[0] ?? player.position);
    if (!pos) continue;

    // Build display name: prefer full_name, fall back to first+last
    const name = player.full_name
      || [player.first_name, player.last_name].filter(Boolean).join(" ");
    if (!name) continue;

    const team = normalizeTeam(player.team);
    entries.push([name, { team: team ?? "FA", pos }]);
  }

  const map = new Map(entries);

  // Persist to localStorage
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      fetchedAt: Date.now(),
      entries,
    }));
  } catch {
    // localStorage quota exceeded or unavailable — ignore, cache is best-effort
  }

  return map;
}

/**
 * Applies fetched roster updates to UNIQUE_PLAYERS in place.
 *
 * Matching strategy (in order):
 *   1. Exact name match (Sleeper's full_name === our player.name)
 *   2. Normalized match (strip trailing suffix periods, collapse whitespace)
 *   3. Suffix-stripped match (ignore Jr/Sr/III/etc entirely on both sides)
 *
 * Updates the `team` field on any matched player whose team has changed.
 *
 * @param {object[]} players — UNIQUE_PLAYERS array (mutated in place)
 * @param {Map<string, {team: string, pos: string}>} updates — from fetchCurrentRosters()
 * @returns {{ updated: number, unchanged: number, unmatched: number }}
 */
export function applyRosterUpdates(players, updates) {
  // Build normalized lookup indices from the Sleeper map
  // normKey → { team, pos }
  const byNorm = new Map();
  const byStripped = new Map();
  for (const [name, data] of updates) {
    const norm = normalizeName(name);
    if (!byNorm.has(norm)) byNorm.set(norm, data);
    const stripped = stripSuffix(norm);
    if (!byStripped.has(stripped)) byStripped.set(stripped, data);
  }

  let updated = 0, unchanged = 0, unmatched = 0;

  for (const player of players) {
    // 1. Exact match
    let match = updates.get(player.name);

    // 2. Normalized match
    if (!match) {
      const norm = normalizeName(player.name);
      match = byNorm.get(norm);
    }

    // 3. Suffix-stripped match
    if (!match) {
      const stripped = stripSuffix(normalizeName(player.name));
      match = byStripped.get(stripped);
    }

    if (!match) {
      unmatched++;
      continue;
    }

    if (match.team && match.team !== player.team) {
      player.team = match.team;
      updated++;
    } else {
      unchanged++;
    }
  }

  return { updated, unchanged, unmatched };
}
